import React, { useState } from 'react';
import {
    Box, Button, Typography, Paper, CircularProgress, Alert, Grid, Link,
    Select, MenuItem, InputLabel, FormControl, InputAdornment, Divider, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    CheckCircle as CheckIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { RankingInvestigadorPeriodoDTO, FileModel } from './types/Ranking';
import ConsultasService from './services/ConsultasService';
import SweetAlertUtil from '../../utils/SweetAlert';

interface SimulationUploadProps {
    onDataLoaded: (inv: any[], uni: any[], cen: any[], prof: any[], conCitaciones: boolean, cargo: string) => void;
    onSkip: () => void;
}

// 1. Cálculo dinámico de años para Mundo Scival
const currentYear = new Date().getFullYear();
const dynamicYearColumns = Array.from({ length: 10 }, (_, i) => ((currentYear - 1) - 10 + i).toString());
const EXPECTED_HEADERS: Record<string, string[]> = {
    investigadores: [
        ...dynamicYearColumns,
        'metrica',
        'QS subject name',
        'QS Faculty Area Name',
        'tipo'
    ],
    centros: [],
    profesores: [
        'CI',
        'scopusID',
        'NOMBRECOMPLETO',
        'DOCENTE',
        'IDENTIFICACION'
    ],
    fwciI: [
        'QS Subject Area',
        'Quartile'
    ],
    fwciQs: [
        'Entity',
        'Field-Weighted Citation Impact',
    ]

};

export const UploadFiles: React.FC<SimulationUploadProps> = ({ onDataLoaded, onSkip }) => {
    const [files, setFiles] = useState<{ [key: string]: File | null }>({
        investigadores: null,
        centros: null,
        profesores: null,
        fwciI: null,
        publicaciones: null,
        horasCentros: null,
        fwciQs: null
    });

    const [selectedAutocitas, setSelectedAutocitas] = useState<string>('S');
    const autocitasOptions = ['S', 'N'];

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [tipo, setTipo] = useState('investigadores');


    const validateExcelFile = (file: File, key: string, expectedHeaders: string[]): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target?.result as ArrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array' });

                    if (key === 'centros' || key === "horasCentros") {
                        const hasAtLeastTwoSheets = workbook.SheetNames.length >= 2;
                        resolve(hasAtLeastTwoSheets);
                        return;
                    }

                    // REGLA PARA OTROS ARCHIVOS: Validar las columnas de la primera hoja
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });

                    if (jsonData.length > 0) {
                        const actualHeaders = jsonData[0];

                        // Normalizamos los encabezados del archivo para buscar fácilmente
                        const normalizedActualHeaders = actualHeaders.map(h =>
                            h ? String(h).trim().toLowerCase() : ''
                        );

                        // Verificamos que TODOS los esperados existan en el archivo, sin importar el orden
                        const isValid = expectedHeaders.every((header) => {
                            return normalizedActualHeaders.includes(header.trim().toLowerCase());
                        });

                        resolve(isValid);
                    } else {
                        resolve(false);
                    }
                } catch (error) {
                    reject(error);
                }
            };

            reader.onerror = () => reject(new Error("Error leyendo el archivo"));
            reader.readAsArrayBuffer(file);
        });
    };

    const handleFileChange = (key: string) => async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const extension = file.name.split('.').pop()?.toLowerCase();

            // 1. Validar extensión según la clave
            if (key === "publicaciones") {
                if (extension !== 'zip') {
                    SweetAlertUtil.alertaError("¡Error!", 'El archivo no es válido. Para publicaciones solo se permiten archivos comprimidos .zip');
                    event.target.value = '';
                    return;
                }
            } else {
                if (extension !== 'xlsx') {
                    SweetAlertUtil.alertaError("¡Error!", 'El archivo no es válido. Solo se permiten archivos Excel con extensión .xlsx');
                    event.target.value = '';
                    return;
                }
            }

            try {
                setLoading(true);

                // 2. Si es publicaciones (.zip), omitimos la lectura del Excel
                if (key !== "publicaciones") {
                    const expectedHeaders = EXPECTED_HEADERS[key];
                    const isValid = await validateExcelFile(file, key, expectedHeaders);

                    if (!isValid) {
                        const mensajeError = key === 'centros' || key === 'horasCentros'
                            ? 'El archivo de Relación de áreas y subáreas QS debe contener al menos 2 hojas de trabajo (sheets).'
                            : `El archivo cargado no cuenta con todas las columnas obligatorias. Estructura esperada (el orden no importa): \n\n[ ${expectedHeaders.join(', ')} ]`;

                        SweetAlertUtil.alertaError("Formato Incorrecto", mensajeError);
                        event.target.value = '';
                        setLoading(false);
                        return;
                    }
                }

                // Guardar el archivo en el estado si pasa las validaciones
                setFiles(prev => ({ ...prev, [key]: file }));
                setError(null);
            } catch (err) {
                SweetAlertUtil.alertaError("¡Error!", 'Hubo un problema al intentar procesar el archivo.');
                event.target.value = '';
            } finally {
                setLoading(false);
            }
        }
    };

    const convertFileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result?.toString().split(',')[1] || '');
            reader.onerror = error => reject(error);
        });
    };

    const getFileModel = async (file: File | null): Promise<FileModel | null> => {
        if (!file) return null;
        const base64 = await convertFileToBase64(file);
        return { fileName: file.name, content: base64 };
    };

    const handleProcessFiles = async () => {
        if (tipo === "investigadores") {
            if (!files.investigadores || !files.centros || !files.profesores || !files.fwciI) {
                setError("Por favor, cargue los 4 archivos Excel requeridos (Scival, QS , Profesores y FWCI) para continuar.");
                return;
            }
        } else if (tipo === "centros") {
            if (!files.publicaciones || !files.horasCentros || !files.fwciQs) {
                setError("Por favor, cargue los 3 archivos requeridos (Publicaciones, Horas y FWCI) para continuar.");
                return;
            }
        }

        setLoading(true);
        setError(null);

        try {
            let payload: any;
            let idRuta = 3;

            if (tipo === "investigadores") {
                const scivalModel = await getFileModel(files.investigadores);
                const qsModel = await getFileModel(files.centros);
                const profModel = await getFileModel(files.profesores);
                const fwciIModel = await getFileModel(files.fwciI); // Transformando el 4to archivo

                payload = {
                    iddecinvrankinginvestigadorperiodo: 0,
                    anio: new Date().getFullYear(),
                    estado: "A",
                    conautocitas: selectedAutocitas,
                    esoficial: "N",
                    publicar: "N",
                    tipocargo: "",
                    mundoscivalref: "",
                    mundoscival: scivalModel,
                    relacionqsref: "",
                    relacionqs: qsModel,
                    listadoprofesoresref: "",
                    listadoprofesores: profModel,
                    // Asegúrate de que tu DTO en C# tenga estas propiedades exactas:
                    fwciiref: "",
                    fwcii: fwciIModel,
                    fechacreacion: new Date().toISOString(),
                    ultimoCambio: new Date().toISOString()
                };

                idRuta = 3;

                const respueta = await ConsultasService.PostPeriodoRanking(idRuta, payload);

                if (respueta.status.isSuccessfully) {
                    SweetAlertUtil.alertaExitoHtml("¡Éxito!", respueta.status.messages);
                    onSkip();
                } else {
                    SweetAlertUtil.alertaAdvertenciaHtml("¡Error!", respueta.status.messages);
                }

            } else if (tipo === "centros") {
                // Conversión de todos los archivos de centros a Base64
                const publicacionesModel = await getFileModel(files.publicaciones);
                const horasModel = await getFileModel(files.horasCentros);
                const fwciQsModel = await getFileModel(files.fwciQs);

                payload = {
                    iddecinvrankingcentroperiodo: 0,
                    anio: new Date().getFullYear(),
                    estado: "A",
                    esoficial: "N",
                    publicar: "N",

                    Publicacionesref: "",
                    Publicaciones: publicacionesModel,

                    Horasinvref: "",
                    Horasinv: horasModel,

                    Fwciref: "",
                    Fwci: fwciQsModel,

                    fechacreacion: new Date().toISOString(),
                    ultimoCambio: new Date().toISOString()
                };

                idRuta = 4;
                const respueta = await ConsultasService.PostRankingUnidades(idRuta, payload);
                if (respueta.status.isSuccessfully) {
                    SweetAlertUtil.alertaExitoHtml("¡Éxito!", respueta.status.messages);
                    onSkip();
                } else {
                    SweetAlertUtil.alertaAdvertenciaHtml("¡Error!", respueta.status.messages);
                }
            }

        } catch (err) {
            console.error("Error al procesar/subir archivos:", err);
            setError("Ocurrió un error al intentar transformar o guardar el ranking en el servidor. Revise la consola.");
        } finally {
            setLoading(false);
        }
    };

    const FileInput = ({ label, fileKey }: { label: string, fileKey: string }) => (
        <Box sx={{
            border: '2px dashed', borderColor: files[fileKey] ? 'success.main' : 'grey.400',
            p: 3, borderRadius: 3, textAlign: 'center',
            bgcolor: files[fileKey] ? '#f0fdf4' : 'background.paper',
            transition: 'all 0.2s', '&:hover': { borderColor: 'primary.main', bgcolor: 'grey.50' },
            height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'
        }}>
            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" gutterBottom>
                {label}
            </Typography>

            <input
                accept={fileKey === "publicaciones" ? ".zip" : ".xlsx"}
                style={{ display: 'none' }}
                id={`file-input-${fileKey}`}
                type="file"
                onChange={handleFileChange(fileKey)}
            />

            <label htmlFor={`file-input-${fileKey}`}>
                <Button
                    variant="outlined"
                    component="span"
                    disabled={loading}
                    startIcon={files[fileKey] ? <CheckIcon /> : <CloudUploadIcon />}
                    color={files[fileKey] ? "success" : "primary"}
                    sx={{
                        mt: 1, textTransform: 'none', borderRadius: 2,
                        borderWidth: files[fileKey] ? 2 : 1,
                        '&:hover': { borderWidth: files[fileKey] ? 2 : 1 }
                    }}
                >
                    {files[fileKey] ? "Archivo Listo" : "Seleccionar Archivo"}
                </Button>
            </label>
            {files[fileKey] && <Typography variant="caption" display="block" sx={{ mt: 1.5, color: 'success.main', fontWeight: 'medium' }}>{files[fileKey]?.name}</Typography>}
        </Box>
    );

    return (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', bgcolor: '#f8f9fa' }}>
            <Paper elevation={4} sx={{ p: { xs: 3, md: 5 }, width: '100%', maxWidth: 1000, borderRadius: 4 }}>

                <Typography variant="h4" fontWeight="bold" textAlign="center" sx={{ mb: 1, color: '#243165' }}>
                    Ranking
                </Typography>

                <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 1 }}>
                    Configure los parámetros y cargue los archivos maestros correspondientes en formato Excel (.xlsx) para generar el reporte.
                </Typography>

                {/* Grupo de Radios para Investigadores y Centros */}
                <FormControl component="fieldset" sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <RadioGroup
                        row
                        aria-label="tipo-reporte"
                        name="tipo-reporte"
                        value={tipo}
                        onChange={(e) => {
                            setTipo(e.target.value);

                            // Reseteamos los archivos cargados al cambiar de opción
                            setFiles({
                                investigadores: null,
                                centros: null,
                                profesores: null,
                                qs: null,
                                publicaciones: null,
                                horasCentros: null,
                                fwciQs: null
                            });

                            // También es buena práctica limpiar cualquier error previo
                            setError(null);
                        }}
                    >
                        <FormControlLabel
                            value="investigadores"
                            control={<Radio sx={{ color: '#243165', '&.Mui-checked': { color: '#243165' } }} />}
                            label="Investigadores"
                            sx={{ mr: 4 }}
                        />
                        <FormControlLabel
                            value="centros"
                            control={<Radio sx={{ color: '#243165', '&.Mui-checked': { color: '#243165' } }} />}
                            label="Centros/Unidades"
                        />
                    </RadioGroup>
                </FormControl>
                <Divider />

                <Grid container spacing={3} justifyContent="center">
                    {tipo == "investigadores" ? (
                        <>
                            <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                                <FileInput label="1. Mundo Scival (.xlsx)" fileKey="investigadores" />
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                                <FileInput label="2. Relación de áreas y subáreas QS (.xlsx)" fileKey="centros" />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FileInput label="3. Listado profesores (.xlsx)" fileKey="profesores" />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FileInput label="4. FWCI QS Subject Areas (.xlsx)" fileKey="fwciI" />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Filtro Autocitas</InputLabel>
                                    <Select
                                        value={selectedAutocitas}
                                        label="Filtro Autocitas"
                                        startAdornment={<InputAdornment position="start"><FilterIcon fontSize="small" /></InputAdornment>}
                                        onChange={(e) => setSelectedAutocitas(e.target.value)}
                                    >
                                        {autocitasOptions.map(op => (
                                            <MenuItem key={op} value={op}>
                                                {op === 'S' ? 'Incluir (Sí)' : 'Excluir (No)'}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} md={12} sx={{ mt: 2 }}>
                                <FileInput label="1. Publicaciones (.zip)" fileKey="publicaciones" />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FileInput label="2. Horas de Inv. Centros y Unidades (.xlsx)" fileKey="horasCentros" />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FileInput label="3. FWCI QS (.xlsx)" fileKey="fwciQs" />
                            </Grid>
                        </>
                    )}
                </Grid>

                <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {error && <Alert severity="error" sx={{ mb: 3, width: '100%' }}>{error}</Alert>}

                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleProcessFiles}
                        disabled={loading}
                        sx={{
                            py: 1.5, px: 6, fontSize: '1.1rem', borderRadius: 3,
                            bgcolor: '#243165', color: '#ffffff !important',
                            '&:hover': { bgcolor: '#1a234a' },
                            boxShadow: '0 4px 12px rgba(36, 49, 101, 0.3)'
                        }}
                    >
                        {loading ? <CircularProgress size={26} color="inherit" /> : "Generar y Guardar Ranking"}
                    </Button>

                    <Link
                        component="button" variant="body2" onClick={onSkip} disabled={loading}
                        sx={{ mt: 2, color: '#243165', textDecoration: 'underline', fontWeight: 'medium', '&:hover': { color: '#1a234a' } }}
                    >
                        Saltar y consultar datos anteriores
                    </Link>
                </Box>
            </Paper>
        </Box>
    );
};